import { useState, useEffect } from "react";
import { AlertCircle, Clock, CheckCircle, XCircle } from "lucide-react";
import { listTasks, type TaskInstance, type TaskOpsStatus, type TaskHealthState } from "../../../api/tasks";
import { getProcessTemplate, type ProcessTemplate } from "../../../api/processes";
import { Badge } from "../../primitives/Badge";
import { cn } from "../../utils/cn";

type ProcessHealthTabProps = {
  templateId: string;
};

type HealthMetrics = {
  overdue: number;
  blocked: number;
  slaExceeded: number;
  unassigned: number;
};

export function ProcessHealthTab({ templateId }: ProcessHealthTabProps) {
  const [template, setTemplate] = useState<ProcessTemplate | null>(null);
  const [tasks, setTasks] = useState<TaskInstance[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [metrics, setMetrics] = useState<HealthMetrics>({
    overdue: 0,
    blocked: 0,
    slaExceeded: 0,
    unassigned: 0,
  });

  useEffect(() => {
    loadTemplate();
  }, [templateId]);

  useEffect(() => {
    if (template) {
      loadTasks();
    }
  }, [template]);

  const loadTemplate = async () => {
    try {
      const data = await getProcessTemplate(templateId);
      setTemplate(data);
    } catch (error: any) {
      console.error("Failed to load process template:", error);
    }
  };

  const loadTasks = async () => {
    if (!template) return;
    setIsLoading(true);
    try {
      const params: any = {
        page: 1,
        page_size: 200,
      };
      if (template.ops_family) {
        // Filter by ops_family if available
      }

      const data = await listTasks(params);
      let filteredTasks = data.items;
      if (template.ops_family) {
        filteredTasks = data.items.filter((t) => t.ops_family === template.ops_family);
      }
      setTasks(filteredTasks);

      // Calculate metrics
      const now = new Date();
      const overdue = filteredTasks.filter(
        (t) => t.status_ops === "in_progress" && t.target_end_date && new Date(t.target_end_date) < now
      ).length;
      const blocked = filteredTasks.filter((t) => t.status_ops === "blocked").length;
      const slaExceeded = filteredTasks.filter(
        (t) => t.health_state === "critical" || t.health_state === "attention"
      ).length;
      const unassigned = 0; // TODO: Calculate unassigned activities

      setMetrics({ overdue, blocked, slaExceeded, unassigned });
    } catch (error: any) {
      console.error("Failed to load tasks:", error);
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return <div className="p-8 text-center text-muted-foreground">Chargement...</div>;
  }

  if (!template) {
    return <div className="p-8 text-center text-muted-foreground">Processus introuvable</div>;
  }

  return (
    <div className="flex flex-col h-full">
      <div className="border-b border-border bg-surface p-4">
        <h3 className="text-md font-medium">Santé du processus</h3>
        <p className="text-sm text-muted-foreground mt-1">
          Vue d'ensemble de l'état de santé des tâches actives
        </p>
      </div>

      <div className="flex-1 overflow-y-auto p-4">
        {/* Metrics Cards */}
        <div className="grid grid-cols-2 gap-4 mb-6">
          <div className="p-4 border border-border rounded-lg bg-surface">
            <div className="flex items-center gap-2 mb-2">
              <Clock size={20} className="text-warning" />
              <span className="text-sm font-medium">En retard</span>
            </div>
            <div className="text-2xl font-bold">{metrics.overdue}</div>
            <p className="text-xs text-muted-foreground mt-1">Tâches dépassant leur date limite</p>
          </div>

          <div className="p-4 border border-border rounded-lg bg-surface">
            <div className="flex items-center gap-2 mb-2">
              <XCircle size={20} className="text-danger" />
              <span className="text-sm font-medium">Bloquées</span>
            </div>
            <div className="text-2xl font-bold">{metrics.blocked}</div>
            <p className="text-xs text-muted-foreground mt-1">Tâches bloquées</p>
          </div>

          <div className="p-4 border border-border rounded-lg bg-surface">
            <div className="flex items-center gap-2 mb-2">
              <AlertCircle size={20} className="text-warning" />
              <span className="text-sm font-medium">SLA dépassé</span>
            </div>
            <div className="text-2xl font-bold">{metrics.slaExceeded}</div>
            <p className="text-xs text-muted-foreground mt-1">Tâches avec SLA dépassé</p>
          </div>

          <div className="p-4 border border-border rounded-lg bg-surface">
            <div className="flex items-center gap-2 mb-2">
              <AlertCircle size={20} className="text-info" />
              <span className="text-sm font-medium">Non assignées</span>
            </div>
            <div className="text-2xl font-bold">{metrics.unassigned}</div>
            <p className="text-xs text-muted-foreground mt-1">Activités non assignées</p>
          </div>
        </div>

        {/* Tasks List */}
        <div>
          <h4 className="text-sm font-medium mb-3">Tâches nécessitant une attention</h4>
          {tasks.length === 0 ? (
            <div className="p-8 text-center text-muted-foreground">
              <CheckCircle size={48} className="mx-auto mb-4 opacity-50" />
              <p>Aucune tâche active</p>
            </div>
          ) : (
            <div className="space-y-2">
              {tasks
                .filter(
                  (t) =>
                    t.status_ops === "blocked" ||
                    (t.status_ops === "in_progress" &&
                      t.target_end_date &&
                      new Date(t.target_end_date) < new Date()) ||
                    t.health_state === "critical"
                )
                .map((task) => (
                  <div
                    key={task.id}
                    className="p-3 border border-border rounded bg-surface hover:bg-surface-subtle transition-colors"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-sm font-medium">Tâche #{task.id.slice(0, 8)}</span>
                          {task.status_ops === "blocked" && (
                            <Badge label="Bloquée" tone="danger" />
                          )}
                          {task.health_state === "critical" && (
                            <Badge label="Critique" tone="danger" />
                          )}
                          {task.target_end_date &&
                            new Date(task.target_end_date) < new Date() &&
                            task.status_ops === "in_progress" && (
                              <Badge label="En retard" tone="warning" />
                            )}
                        </div>
                        <div className="text-xs text-muted-foreground">
                          {task.target_end_date && (
                            <span>
                              Échéance: {new Date(task.target_end_date).toLocaleDateString("fr-CA")}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
